import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Text, VerticalSpacing, useMedia } from '../../../../Boneyard';
import Link from '@g4/next-furniture/Link';
import TravelerItems from './traveler/TravelerItems.js';
import get from 'lodash/get';
import { getPrice } from '@g4/next-furniture/Price/Price';
import isNumber from 'lodash/isNumber';
import { useTranslations } from '../../../../../graphql/components/TranslationsProvider';
import useBookingSteps from '../../../../../hooks/useBookingSteps';
import useCompletedStep from '../../../../../hooks/useCompletedStep';
import { withValue, hook } from '@g4/automation-hooks';
import { interpolate } from '../../../../../utils/stringUtils';
import { TrackClick } from '@g4/trackster';

import SEAT_ICON from '../../../../../images/ic-seat.svg';
import BAG_ICON from '../../../../../images/ic-car-bags-capacity.svg';
import EXTRAS_ICON from '../../../../../images/ic-extras.svg';
import { grey900 } from '../../../../../theme/theme/colors';
import { ancillaryTypes } from '../../../../../pageComponents/confirmationPage/flightInformation/constants';

const PET_IN_CABIN_SSR_CODE = 'PETC';

const propTypes = {
  automationHook: PropTypes.string,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  middleName: PropTypes.string,
  suffix: PropTypes.string,
  seats: PropTypes.array,
  bags: PropTypes.array,
  extras: PropTypes.array,
  flights: PropTypes.array,
  travelerNumber: PropTypes.number,
  ssrs: PropTypes.array,
  seatSelection: PropTypes.bool,
  isTripSummary: PropTypes.bool,
  showSeatsLink: PropTypes.bool,
  showBagsLink: PropTypes.bool,
  hasBundledSeats: PropTypes.bool,
  hasBundledBagsAndExtras: PropTypes.bool,
};

const defaultProps = {
  seats: [],
  bags: [],
  extras: [],
  flights: [],
  editStep: {},
  travelerNumber: 1,
  ssrs: [],
};

const Container = styled(Box)`
  background: ${props => props.theme.colors.common.light};
  & + & {
    border-top: 2px solid ${props => props.theme.colors.cart.travelerSeparator};
    &::before {
      border-top: 2;
    }
  }
`;

const ItemHeading = styled(Text)`
  width: ${props =>
    props.isTripSummary
      ? props.isMobile
        ? props.isBags
          ? '55px'
          : props.isExtras
          ? '65px'
          : '59px'
        : props.isBags
        ? '62px'
        : props.isExtras
        ? '82px'
        : '68px'
      : '75px'};
  text-transform: uppercase;
`;

const SectionContainer = styled(Box)`
  border-top: 1px solid ${props => props.theme.colors.cart.travelerSeparator};
`;

const MarginBox = styled(Box)`
  display: flex;
  margin-top: ${props => (props.isTablet ? '0px' : '10px')};
  margin-left: auto;
  min-width: ${props => (!props.isTablet ? '10%' : '0')};
  justify-content: ${props => (!props.isTablet ? 'flex-end' : 'flex-start')};
`;

const EditAnchor = styled.a`
  z-index: 1;
  font-size: ${props =>
    props.isTripSummary
      ? props.isMobile
        ? '12px'
        : '14px'
      : props.theme.toRem(12)};
  text-decoration: underline;
  text-transform: uppercase;
  color: ${props => props.theme.colors.cart.item.edit};
`;

const SeatIconContainer = styled.div`
  width: 11px;
  height: 19px;
  max-width: 100%;
  max-height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 14px;
  background-image: url(${SEAT_ICON});
`;

const BagIconContainer = styled.div`
  width: 20px;
  height: 19px;
  max-width: 100%;
  max-height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 13px;
  background-image: url(${BAG_ICON});
`;

const TripflexItem = styled(Box)`
  padding-left: 75px;
`;

const ExtrasIconContainer = styled.div`
  width: 14px;
  height: 20px;
  max-width: 100%;
  max-height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 15px;
  margin-right: 3px;
  background-image: url(${EXTRAS_ICON});
`;

const totalAmountsArray = amountsArray =>
  amountsArray.reduce((acc, item) => acc + item, 0);

const travelerItemsGenericTypeTotal = travelerItemsGenericType => {
  const amountsArray = travelerItemsGenericType.map(
    item => get(item, 'price.amount') || 0
  );
  return totalAmountsArray(amountsArray);
};

const getUnbundledItems = items =>
  items.filter(item => !item.isBundledAncillaryIncluded);

const getItemsForFlight = (items, flight, extras = [], flights = []) => {

  const filteredItems = items.filter(item => {
    if (item.flightId === flight) {
      return true;
    }

    if (item.__typename === 'ItineraryAncillaryOrderItem') {
      const hasBundledExtrasForCurrentFlight = extras.some(extra =>
        extra.flightId === flight && extra.isBundledAncillaryIncluded
      );

      return hasBundledExtrasForCurrentFlight;
    }

    return false;
  });

  const seenTripFlex = new Set();
  return filteredItems.filter(item => {
    if (item.ancillaryType === 'TRIP_FLEX') {
      if (seenTripFlex.has('TRIP_FLEX')) {
        return false;
      }
      seenTripFlex.add('TRIP_FLEX');
    }
    return true;
  });
};

const getPetInCabinForFlight = (ssrs, flightId) =>
  ssrs.find(
    ssr => ssr.flightId === flightId && ssr.code === PET_IN_CABIN_SSR_CODE
  );

const getTripflexForFlight = extras => {
  const tripflexFlight = extras.find(extra => {
    return extra.ancillaryType === 'TRIP_FLEX';
  });
  return tripflexFlight;
};

const getOnboardEntertainmentForFlight = extras => {
  const onboardEntertainmentForFlight = extras.find(extra => {
    return extra.ancillaryType === ancillaryTypes.ONBOARD_ENTERTAINMENT;
  });
  return onboardEntertainmentForFlight;
};

const getPriceForSection = items => {
  const amount = travelerItemsGenericTypeTotal(items);
  const { price } = getPrice(amount);
  return { amount: amount, price: price };
};

// for seats just get subtotal since XT (if any) is included in govenment fees
const getPriceForSeats = seatItems => {
  const amountsArray = seatItems.map(
    item => get(item, 'seatPrice.subtotal') || get(item, 'price.amount') || 0
  );
  const amount = totalAmountsArray(amountsArray);
  const { price } = getPrice(amount);
  return { amount: amount, price: price };
};

const TrackableLink = React.forwardRef(
  ({ trackingLabel, isTripSummary, ...props }, ref) => (
    <TrackClick
      category="site_click_tracking"
      action={isTripSummary ? 'Review and Payment' : 'Trip Summary'}
      label={trackingLabel}
    >
      <EditAnchor {...props} ref={ref} />
    </TrackClick>
  )
);

TrackableLink.propTypes = {
  children: PropTypes.node.isRequired,
  trackingLabel: PropTypes.string.isRequired,
  isTripSummary: PropTypes.bool,
};

const Traveler = ({
  automationHook,
  firstName,
  lastName,
  middleName,
  suffix,
  seats,
  bags,
  extras,
  flights,
  travelerNumber,
  ssrs,
  seatSelection,
  isTripSummary,
  showSeatsLink,
  showBagsLink,
  hasBundledSeats,
  hasBundledBagsAndExtras,
}) => {
  const departingFlight = flights[0];
  const returningFlight = flights[1];

  const seatsPrice = getPriceForSeats(seats);
  const travelerPrice =
    seatsPrice + getPriceForSection(bags.concat(extras, ssrs));
  const travelerPriceWithoutTripFlex = getPriceForSection(
    seats.concat(
      bags,
      extras.filter(extra => extra.ancillaryType !== 'TRIP_FLEX'),
      ssrs
    )
  );

  const bagsPrice = getPriceForSection(bags);

  const areNonBundledSeats = !!getUnbundledItems(seats).length;
  const areNonBundledBags = !!getUnbundledItems(bags).length;

  const petInCabinSelections = ssrs.filter(
    ssr => ssr.code === PET_IN_CABIN_SSR_CODE
  );

  const areNonBundledExtras = !![
    ...getUnbundledItems(extras),
    ...petInCabinSelections,
  ].length;

  const shouldDisplayIncludedLabel = item => {
    return (
      (item?.isBundledAncillaryIncluded && item?.price?.amount === 0) ||
      (!areNonBundledExtras && !item) ||
      (item.ancillaryType === 'TRIP_FLEX' && travelerNumber > 1)
    );
  };

  const extrasSectionPrice = getPriceForSection([
    ...extras,
    ...petInCabinSelections,
  ]);

  const extrasSectionPriceWithoutTripFlex = getPriceForSection([
    ...extras.filter(extra => extra.ancillaryType !== 'TRIP_FLEX'),
    ...petInCabinSelections,
  ]);

  const mq = useMedia();
  const isMobile = mq.lt('md');
  const isTablet = mq.lt('lg');

  const translations = useTranslations();
  const {
    cartItemAddLinkLabel,
    cartItemEditLinkLabel,
    travelerSeatsLabel,
    travelerBagsLabel,
    travelerExtrasLabel,
    cartTripFlexLabel,
    travelerIncludedLabel,
    cartIndividualTravelerLabel,
    cartOnboardEntertainmentLabel,
    cartEnhancedTripFlexLabelHTML,
  } = isTripSummary ? translations.paymentPage : translations.cart;

  const [isSeatsStepCompleted] = useCompletedStep('seats');
  const { steps, activeStep } = useBookingSteps();
  const getStep = key => steps.find(x => x.key === key);
  const seatsStep = getStep('seats');
  const bagsStep = getStep('bags');

  const extrasForDeparting = getItemsForFlight(
    extras,
    departingFlight.flight.id,
    flights
  );
  const petInCabinForDeparting = getPetInCabinForFlight(
    ssrs,
    departingFlight.flight.id
  );
  const extrasForReturning =
    returningFlight && getItemsForFlight(extras, returningFlight.flight.id, flights);
  const petInCabinForReturning =
    returningFlight && getPetInCabinForFlight(ssrs, returningFlight.flight.id);

  const tripflexForFlight = getTripflexForFlight(extras);

  const onboardEntertainmentForFlight = getOnboardEntertainmentForFlight(
    extras
  );

  return (
    <Container
      pl={isTripSummary && isTablet ? 2 : 6}
      pr={isTripSummary && isTablet ? 2 : 6}
      automationHook={automationHook}
    >
      <Box pt={2} pb={2} d="flex" justifyContent="space-between">
        {isTripSummary ? (
          <Text
            size={isMobile ? 14 : 18}
            weight={600}
            automationHook={withValue(automationHook, 'name')}
          >
            {firstName} {middleName} {lastName}{' '}
            {suffix !== 'NOT_SELECTED' ? suffix : ''}
          </Text>
        ) : (
          <Text
            size={mq.gte('md') ? 16 : 14}
            weight={600}
            automationHook={withValue(automationHook, 'name')}
          >
            {interpolate(cartIndividualTravelerLabel, {
              number: travelerNumber,
            })}{' '}
            {firstName} {lastName.charAt(0)}
          </Text>
        )}
        {isNumber(travelerPrice.amount) && (
          <Text
            size={mq.gte('md') ? 18 : 16}
            weight={500}
            automationHook={withValue(automationHook, 'price')}
          >
            {travelerNumber > 1
              ? travelerPriceWithoutTripFlex.price
              : travelerPrice.price}
          </Text>
        )}
      </Box>
      {!!seats.length || seatSelection ? (
        <SectionContainer>
          <Box
            d="flex"
            justifyContent={isTripSummary ? 'flex-start' : 'space-between'}
            alignItems={
              isTripSummary ? (isTablet ? 'flex-start' : 'center') : 'flex-end'
            }
            direction={isTripSummary && isTablet ? 'column' : 'row'}
          >
            {isTripSummary && !isTablet ? <SeatIconContainer /> : ''}
            <Box
              d="flex"
              pt={isTripSummary ? (isTablet ? 2 : 0) : 2}
              ml={isTripSummary ? (isTablet ? 0 : 3) : 0}
              w={isTripSummary && isTablet ? '100%' : 'auto'}
            >
              <ItemHeading
                size={
                  isTripSummary ? (isMobile ? 15 : 18) : mq.gte('md') ? 15 : 13
                }
                lh={isTripSummary ? (isMobile ? 1.13 : 0.94) : 1}
                weight={isTripSummary ? 500 : 400}
                automationHook={withValue(automationHook, 'seats_label')}
                isTripSummary={isTripSummary}
                isMobile={isMobile}
              >
                {travelerSeatsLabel}
              </ItemHeading>
              <Box d="flex">
                {activeStep?.key !== 'seats' && showSeatsLink && (
                  <Link href={seatsStep.href} as={seatsStep.as} clientRoute>
                    {({ anchorProps }) => (
                      <TrackableLink
                        isTripSummary={isTripSummary}
                        isMobile={isMobile}
                        {...anchorProps}
                        {...hook(automationHook, 'seats_edit')}
                        trackingLabel={`Edit Seats ${travelerNumber}`}
                      >
                        {cartItemEditLinkLabel}
                      </TrackableLink>
                    )}
                  </Link>
                )}
              </Box>
              {isTripSummary && isTablet ? (
                <MarginBox isTablet={isTablet}>
                  {isNumber(seatsPrice.amount) && seatsPrice.amount > 0 ? (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'seats_price')}
                    >
                      {seatsPrice.price}
                    </Text>
                  ) : (
                    hasBundledSeats && (
                      <Text
                        size={16}
                        automationHook={withValue(
                          automationHook,
                          'seats_price'
                        )}
                      >
                        {travelerIncludedLabel}
                      </Text>
                    )
                  )}
                </MarginBox>
              ) : (
                ''
              )}
            </Box>
            {isTripSummary ? (
              <Box
                d="flex"
                align-items="flex-end"
                w="100%"
                direction={isTablet ? 'column' : 'row'}
                mb={isTablet ? 2 : 0}
                mt={isTablet ? 1 : 0}
                max-width="80%"
                ml={isTablet ? 0 : 1}
              >
                <Box w={isTablet ? '100%' : '50%'}>
                  <TravelerItems
                    automationHook={withValue(automationHook, 'seats')}
                    flight={departingFlight.flight}
                    items={getItemsForFlight(seats, departingFlight.flight.id, extras, flights)}
                    type="seats"
                    leg="departing"
                    areNonBundledItems={areNonBundledSeats}
                    isTripSummary={isTripSummary}
                    returningFlightExists={!!returningFlight}
                  />
                </Box>
                {returningFlight && (
                  <Box w={isTablet ? '100%' : '50%'}>
                    <TravelerItems
                      automationHook={withValue(automationHook, 'seats')}
                      flight={returningFlight.flight}
                      items={getItemsForFlight(seats, returningFlight.flight.id, extras, flights)}
                      type="seats"
                      leg="returning"
                      areNonBundledItems={areNonBundledSeats}
                      isTripSummary={isTripSummary}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              ''
            )}
            {isTablet ? (
              isTripSummary ? (
                ''
              ) : isNumber(seatsPrice.amount) && seatsPrice.amount > 0 ? (
                <Text
                  size={mq.gte('md') ? 14 : 12}
                  weight={500}
                  automationHook={withValue(automationHook, 'seats_price')}
                >
                  {seatsPrice.price}
                </Text>
              ) : (
                hasBundledSeats && (
                  <Text
                    lh={1.1}
                    size={mq.gte('md') ? 14 : 12}
                    automationHook={withValue(automationHook, 'seats_price')}
                  >
                    {travelerIncludedLabel}
                  </Text>
                )
              )
            ) : isTripSummary ? (
              <MarginBox isTablet={isTablet}>
                {isNumber(seatsPrice.amount) && seatsPrice.amount > 0 ? (
                  <Text
                    size={16}
                    automationHook={withValue(automationHook, 'seats_price')}
                  >
                    {seatsPrice.price}
                  </Text>
                ) : (
                  hasBundledSeats && (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'seats_price')}
                    >
                      {travelerIncludedLabel}
                    </Text>
                  )
                )}
              </MarginBox>
            ) : (
              <MarginBox isTablet={isTablet}>
                {isNumber(seatsPrice.amount) && seatsPrice.amount > 0 ? (
                  <Text
                    size={mq.gte('md') ? 14 : 12}
                    weight={500}
                    automationHook={withValue(automationHook, 'seats_price')}
                  >
                    {seatsPrice.price}
                  </Text>
                ) : (
                  hasBundledSeats && (
                    <Text
                      lh={1.1}
                      size={mq.gte('md') ? 14 : 12}
                      automationHook={withValue(automationHook, 'seats_price')}
                    >
                      {travelerIncludedLabel}
                    </Text>
                  )
                )}
              </MarginBox>
            )}
          </Box>
          {isTripSummary ? (
            ''
          ) : (
            <div>
              <VerticalSpacing size={2} />
              <TravelerItems
                automationHook={withValue(automationHook, 'seats')}
                flight={departingFlight.flight}
                items={getItemsForFlight(seats, departingFlight.flight.id, extras, flights)}
                type="seats"
                leg="departing"
                areNonBundledItems={areNonBundledSeats}
                isTripSummary={isTripSummary}
              />
              {returningFlight && (
                <TravelerItems
                  automationHook={withValue(automationHook, 'seats')}
                  flight={returningFlight.flight}
                  items={getItemsForFlight(seats, returningFlight.flight.id, extras, flights)}
                  type="seats"
                  leg="returning"
                  areNonBundledItems={areNonBundledSeats}
                  isTripSummary={isTripSummary}
                />
              )}
              <VerticalSpacing size={2} />
            </div>
          )}
        </SectionContainer>
      ) : (
        <SectionContainer>
          <Box d="flex" pt={2}>
            <ItemHeading
              size={mq.gte('md') ? 15 : 13}
              automationHook={withValue(automationHook, 'seats_label')}
            >
              {travelerSeatsLabel}
            </ItemHeading>
            {activeStep?.key !== 'seats' && showSeatsLink && (
              <Box d="flex">
                <Link href={seatsStep.href} as={seatsStep.as} clientRoute>
                  {({ anchorProps }) => (
                    <TrackableLink
                      {...anchorProps}
                      {...hook(automationHook, 'seats_edit')}
                      trackingLabel={`Add Seats ${travelerNumber}`}
                    >
                      {cartItemAddLinkLabel}
                    </TrackableLink>
                  )}
                </Link>
              </Box>
            )}
          </Box>
          <VerticalSpacing size={2} />
        </SectionContainer>
      )}

      {bags.length ? (
        <SectionContainer>
          <Box
            d="flex"
            justifyContent={isTripSummary ? 'flex-start' : 'space-between'}
            alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
            direction={isTripSummary && isTablet ? 'column' : 'row'}
          >
            {isTripSummary && !isTablet ? <BagIconContainer /> : ''}
            <Box
              d="flex"
              pt={isTripSummary ? (isTablet ? 2 : 0) : 2}
              ml={isTripSummary ? (isTablet ? 0 : 2) : 0}
              w={isTripSummary ? (isTablet ? '100%' : 'auto') : '100%'}
              mt={isTripSummary ? (isTablet ? 0 : 2) : 0}
            >
              <Box d="flex">
                <ItemHeading
                  size={
                    isTripSummary
                      ? isMobile
                        ? 15
                        : 18
                      : mq.gte('md')
                      ? 15
                      : 13
                  }
                  lh={isTripSummary ? (isMobile ? 1.13 : 0.94) : 1}
                  weight={isTripSummary ? 500 : 400}
                  automationHook={withValue(automationHook, 'bags_label')}
                  isTripSummary={isTripSummary}
                  isMobile={isMobile}
                  isBags={true}
                >
                  {travelerBagsLabel}
                </ItemHeading>
                {isSeatsStepCompleted &&
                  activeStep?.key !== 'bags' &&
                  showBagsLink && (
                    <Box d="flex">
                      <Link href={bagsStep.href} as={bagsStep.as} clientRoute>
                        {({ anchorProps }) => (
                          <TrackableLink
                            isTripSummary={isTripSummary}
                            isMobile={isMobile}
                            {...anchorProps}
                            {...hook(automationHook, 'bags_edit')}
                            trackingLabel={`Edit Bags ${travelerNumber}`}
                          >
                            {cartItemEditLinkLabel}
                          </TrackableLink>
                        )}
                      </Link>
                    </Box>
                  )}
              </Box>
              {isTripSummary ? (
                isTablet ? (
                  <MarginBox isTablet={isTablet}>
                    {isNumber(bagsPrice.amount) && bagsPrice.amount > 0 ? (
                      <Text
                        size={16}
                        automationHook={withValue(automationHook, 'bags_price')}
                      >
                        {bagsPrice.price}
                      </Text>
                    ) : (
                      hasBundledBagsAndExtras && (
                        <Text
                          size={16}
                          automationHook={withValue(
                            automationHook,
                            'bags_price'
                          )}
                        >
                          {travelerIncludedLabel}
                        </Text>
                      )
                    )}
                  </MarginBox>
                ) : (
                  ''
                )
              ) : (
                <Box d="flex" justifyContent="flex-end" w="100%">
                  {isNumber(bagsPrice.amount) && bagsPrice.amount > 0 ? (
                    <Text
                      size={mq.gte('md') ? 14 : 12}
                      weight={500}
                      automationHook={withValue(automationHook, 'bags_price')}
                    >
                      {bagsPrice.price}
                    </Text>
                  ) : (
                    hasBundledBagsAndExtras && (
                      <Text
                        lh={1.1}
                        size={mq.gte('md') ? 14 : 12}
                        automationHook={withValue(automationHook, 'bags_price')}
                      >
                        {travelerIncludedLabel}
                      </Text>
                    )
                  )}
                </Box>
              )}
            </Box>
            {isTripSummary ? (
              <Box
                d="flex"
                align-items="flex-end"
                w="100%"
                direction={isTablet ? 'column' : 'row'}
                mb={isTablet ? 2 : 0}
                mt={isTablet ? 1 : 0}
                max-width="80%"
                ml={isTablet ? 0 : 1}
              >
                <Box w={isTablet ? '100%' : '50%'}>
                  {!!getItemsForFlight(bags, departingFlight.flight.id)
                    .length && (
                    <TravelerItems
                      automationHook={withValue(automationHook, 'bags')}
                      flight={departingFlight.flight}
                      items={getItemsForFlight(bags, departingFlight.flight.id, extras, flights)}
                      type="bags"
                      leg="departing"
                      areNonBundledItems={areNonBundledBags}
                      isTripSummary={isTripSummary}
                      returningFlightExists={!!returningFlight}
                    />
                  )}
                </Box>
                {returningFlight && (
                  <Box w={isTablet ? '100%' : '50%'}>
                    {!!getItemsForFlight(bags, returningFlight.flight.id)
                      .length && (
                      <TravelerItems
                        automationHook={withValue(automationHook, 'bags')}
                        flight={returningFlight.flight}
                        items={getItemsForFlight(bags, returningFlight.flight.id, extras, flights)}
                        type="bags"
                        leg="returning"
                        areNonBundledItems={areNonBundledBags}
                        isTripSummary={isTripSummary}
                      />
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              ''
            )}
            {isTripSummary && !isTablet ? (
              <MarginBox isTablet={isTablet}>
                {isNumber(bagsPrice.amount) && bagsPrice.amount > 0 ? (
                  <Text
                    size={16}
                    automationHook={withValue(automationHook, 'bags_price')}
                  >
                    {bagsPrice.price}
                  </Text>
                ) : (
                  hasBundledBagsAndExtras && (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'bags_price')}
                    >
                      {travelerIncludedLabel}
                    </Text>
                  )
                )}
              </MarginBox>
            ) : (
              ''
            )}
          </Box>
          {isTripSummary ? (
            ''
          ) : (
            <div>
              <VerticalSpacing size={2} />
              {!!getItemsForFlight(bags, departingFlight.flight.id, extras, flights).length && (
                <TravelerItems
                  automationHook={withValue(automationHook, 'bags')}
                  flight={departingFlight.flight}
                  items={getItemsForFlight(bags, departingFlight.flight.id, extras, flights)}
                  type="bags"
                  leg="departing"
                  areNonBundledItems={areNonBundledBags}
                  isTripSummary={isTripSummary}
                />
              )}
              {returningFlight &&
                !!getItemsForFlight(bags, returningFlight.flight.id, extras, flights).length && (
                  <TravelerItems
                    automationHook={withValue(automationHook, 'bags')}
                    flight={returningFlight.flight}
                    items={getItemsForFlight(bags, returningFlight.flight.id, extras, flights)}
                    type="bags"
                    leg="returning"
                    areNonBundledItems={areNonBundledBags}
                    isTripSummary={isTripSummary}
                  />
                )}
              <VerticalSpacing size={2} />
            </div>
          )}
        </SectionContainer>
      ) : (
        <SectionContainer>
          <Box d="flex" pt={2}>
            <ItemHeading
              size={mq.gte('md') ? 15 : 13}
              automationHook={withValue(automationHook, 'bags_label')}
            >
              {travelerBagsLabel}
            </ItemHeading>
            {activeStep?.key !== 'bags' &&
              isSeatsStepCompleted &&
              showBagsLink && (
                <Box d="flex">
                  <Link href={bagsStep.href} as={bagsStep.as} clientRoute>
                    {({ anchorProps }) => (
                      <TrackableLink
                        {...anchorProps}
                        {...hook(automationHook, 'bags_edit')}
                        trackingLabel={`Add Bags ${travelerNumber}`}
                      >
                        {cartItemAddLinkLabel}
                      </TrackableLink>
                    )}
                  </Link>
                </Box>
              )}
          </Box>
          <VerticalSpacing size={2} />
        </SectionContainer>
      )}
      {!!extras.length || !!petInCabinSelections.length ? (
        <SectionContainer>
          <Box
            d="flex"
            justifyContent={isTripSummary ? 'flex-start' : 'space-between'}
            alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
            direction={isTripSummary && isTablet ? 'column' : 'row'}
          >
            {isTripSummary && !isTablet ? <ExtrasIconContainer /> : ''}
            <Box
              d="flex"
              pt={isTripSummary ? (isTablet ? 2 : 0) : 2}
              ml={isTripSummary ? (isTablet ? 0 : 2) : 0}
              w={isTripSummary ? (isTablet ? '100%' : 'auto') : '100%'}
              mt={isTripSummary ? (isTablet ? 0 : 2) : 0}
            >
              <Box d="flex">
                <ItemHeading
                  size={
                    isTripSummary
                      ? isMobile
                        ? 15
                        : 18
                      : mq.gte('md')
                      ? 15
                      : 13
                  }
                  lh={isTripSummary ? (isMobile ? 1.13 : 0.94) : 1}
                  weight={isTripSummary ? 500 : 400}
                  automationHook={withValue(automationHook, 'extras_label')}
                  isTripSummary={isTripSummary}
                  isMobile={isMobile}
                  isExtras={true}
                >
                  {travelerExtrasLabel}
                </ItemHeading>
                {isSeatsStepCompleted &&
                  activeStep?.key !== 'bags' &&
                  showBagsLink && (
                    <Box d="flex">
                      <Link href={bagsStep.href} as={bagsStep.as} clientRoute>
                        {({ anchorProps }) => (
                          <TrackableLink
                            isTripSummary={isTripSummary}
                            isMobile={isMobile}
                            {...anchorProps}
                            {...hook(automationHook, 'extras_edit')}
                            trackingLabel={`Edit Extras ${travelerNumber}`}
                          >
                            {cartItemEditLinkLabel}
                          </TrackableLink>
                        )}
                      </Link>
                    </Box>
                  )}
              </Box>
              {isTripSummary ? (
                isTablet ? (
                  <MarginBox isTablet={isTablet}>
                    {travelerNumber > 1 ? (
                      isNumber(extrasSectionPrice.amount) &&
                      extrasSectionPrice.amount > 0 ? (
                        <Text
                          size={16}
                          automationHook={withValue(
                            automationHook,
                            'extras_price'
                          )}
                        >
                          {extrasSectionPrice.price}
                        </Text>
                      ) : (
                        <Text
                          size={16}
                          automationHook={withValue(
                            automationHook,
                            'extras_price'
                          )}
                        >
                          {travelerIncludedLabel}
                        </Text>
                      )
                    ) : isNumber(extrasSectionPrice.amount) &&
                      extrasSectionPrice.amount > 0 ? (
                      <Text
                        size={16}
                        automationHook={withValue(
                          automationHook,
                          'extras_price'
                        )}
                      >
                        {extrasSectionPrice.price}
                      </Text>
                    ) : (
                      <Text
                        size={16}
                        automationHook={withValue(
                          automationHook,
                          'extras_price'
                        )}
                      >
                        {travelerIncludedLabel}
                      </Text>
                    )}
                  </MarginBox>
                ) : (
                  ''
                )
              ) : (
                <Box d="flex" justifyContent="flex-end" w="100%">
                  {isNumber(extrasSectionPrice.amount) &&
                  extrasSectionPrice.amount > 0 ? (
                    <>
                      {tripflexForFlight &&
                      extras.length === 1 &&
                      travelerNumber > 1 ? (
                        <Text
                          size={mq.gte('md') ? 14 : 12}
                          weight={500}
                          automationHook={withValue(
                            automationHook,
                            'extras_price'
                          )}
                        >
                          {travelerIncludedLabel}
                        </Text>
                      ) : travelerNumber > 1 ? (
                        <Text
                          size={mq.gte('md') ? 14 : 12}
                          weight={500}
                          automationHook={withValue(
                            automationHook,
                            'extras_price'
                          )}
                        >
                          {extrasSectionPrice.price}
                        </Text>
                      ) : (
                        <Text
                          size={mq.gte('md') ? 14 : 12}
                          weight={500}
                          automationHook={withValue(
                            automationHook,
                            'extras_price'
                          )}
                        >
                          {extrasSectionPrice.price}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text
                      lh={1.1}
                      size={mq.gte('md') ? 14 : 12}
                      automationHook={withValue(automationHook, 'extras_price')}
                    >
                      {travelerIncludedLabel}
                    </Text>
                  )}
                </Box>
              )}
            </Box>
            {isTripSummary ? (
              <Box
                d="flex"
                align-items="flex-end"
                w="100%"
                direction={isTablet ? 'column' : 'row'}
                mb={isTablet ? 2 : 0}
                mt={isTablet ? 1 : 0}
                max-width="80%"
                ml={isTablet ? 0 : -3}
              >
                <Box w={isTablet ? '100%' : '50%'}>
                  {(extrasForDeparting.length || petInCabinForDeparting) && (
                    <TravelerItems
                      automationHook={withValue(automationHook, 'extras')}
                      flight={departingFlight.flight}
                      items={extrasForDeparting}
                      petInCabin={petInCabinForDeparting}
                      type="extras"
                      leg="departing"
                      areNonBundledItems={areNonBundledExtras}
                      isTripSummary={isTripSummary}
                      travelerNumber={travelerNumber}
                      returningFlightExists={!!returningFlight}
                    />
                  )}
                </Box>
                {returningFlight && (
                  <Box w={isTablet ? '100%' : '50%'}>
                    {(!!extrasForReturning.length || !!petInCabinForReturning) && (
                      <TravelerItems
                        automationHook={withValue(automationHook, 'extras')}
                        flight={returningFlight.flight}
                        items={extrasForReturning}
                        petInCabin={petInCabinForReturning}
                        type="extras"
                        leg="returning"
                        areNonBundledItems={areNonBundledExtras}
                        isTripSummary={isTripSummary}
                        travelerNumber={travelerNumber}
                      />
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              ''
            )}
            {isTripSummary && !isTablet ? (
              travelerNumber > 1 ? (
                <MarginBox isTablet={isTablet}>
                  {isNumber(extrasSectionPrice.amount) &&
                  extrasSectionPrice.amount > 0 ? (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'extras_price')}
                    >
                      {extrasSectionPrice.price}
                    </Text>
                  ) : (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'extras_price')}
                    >
                      {travelerIncludedLabel}
                    </Text>
                  )}
                </MarginBox>
              ) : (
                <MarginBox isTablet={isTablet}>
                  {isNumber(extrasSectionPrice.amount) &&
                  extrasSectionPrice.amount > 0 ? (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'extras_price')}
                    >
                      {extrasSectionPrice.price}
                    </Text>
                  ) : (
                    <Text
                      size={16}
                      automationHook={withValue(automationHook, 'extras_price')}
                    >
                      {travelerIncludedLabel}
                    </Text>
                  )}
                </MarginBox>
              )
            ) : (
              ''
            )}
          </Box>
          {isTripSummary ? (
            ''
          ) : (
            <div>
              <VerticalSpacing size={2} />
              {onboardEntertainmentForFlight && (
                <>
                  <Box d="flex" justifyContent="space-between">
                    <TripflexItem>
                      <Text
                        weight={500}
                        color={`${grey900}`}
                        size={13}
                        lh={1.5}
                        automationHook={withValue(
                          automationHook,
                          'extras_onboard_entertainment_label'
                        )}
                      >
                        {cartOnboardEntertainmentLabel}
                      </Text>
                    </TripflexItem>

                    <Box>
                      <Text
                        size={14}
                        automationHook={withValue(
                          automationHook,
                          'extras_onboard_entertainment_include'
                        )}
                      >
                        {travelerIncludedLabel}
                      </Text>
                    </Box>
                  </Box>
                  <VerticalSpacing size={1} />
                </>
              )}
              {(extrasForDeparting.length || petInCabinForDeparting) && (
                <TravelerItems
                  automationHook={withValue(automationHook, 'extras')}
                  flight={departingFlight.flight}
                  items={extrasForDeparting}
                  petInCabin={petInCabinForDeparting}
                  type="extras"
                  leg="departing"
                  areNonBundledItems={areNonBundledExtras}
                  isTripSummary={isTripSummary}
                />
              )}
              {returningFlight &&
                (!!extrasForReturning.length || !!petInCabinForReturning) && (
                  <TravelerItems
                    automationHook={withValue(automationHook, 'extras')}
                    flight={returningFlight.flight}
                    items={extrasForReturning}
                    petInCabin={petInCabinForReturning}
                    type="extras"
                    leg="returning"
                    areNonBundledItems={areNonBundledExtras}
                    isTripSummary={isTripSummary}
                  />
                )}
            </div>
          )}
        </SectionContainer>
      ) : (
        <SectionContainer>
          <Box d="flex" pt={2}>
            <ItemHeading
              size={mq.gte('md') ? 15 : 13}
              automationHook={withValue(automationHook, 'extras_label')}
            >
              {travelerExtrasLabel}
            </ItemHeading>
            {activeStep?.key !== 'bags' &&
              isSeatsStepCompleted &&
              showBagsLink && (
                <Box d="flex">
                  <Link href={bagsStep.href} as={bagsStep.as} clientRoute>
                    {({ anchorProps }) => (
                      <TrackableLink
                        {...anchorProps}
                        {...hook(automationHook, 'extras_edit')}
                        trackingLabel={`Add Extras ${travelerNumber}`}
                      >
                        {cartItemAddLinkLabel}
                      </TrackableLink>
                    )}
                  </Link>
                </Box>
              )}
          </Box>
          <VerticalSpacing size={2} />
        </SectionContainer>
      )}
    </Container>
  );
};

Traveler.propTypes = propTypes;
Traveler.defaultProps = defaultProps;

export default Traveler;
